import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Row,
  Col,
  Badge,
  Spinner,
  Alert,
  Modal,
  Form,
} from "react-bootstrap";
import { FaMoneyBillWave } from "react-icons/fa";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

const CompOffDisplayForm = () => {
  const storedUser = JSON.parse(localStorage.getItem("user")) || {};
  const employeeId = storedUser.employeeId;
  const isAdmin = storedUser.role === 4; // Assuming role 4 is admin

  // State for comp-off records
  const [compOffRecords, setCompOffRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // State for cash amount modal
  const [showCashModal, setShowCashModal] = useState(false);
  const [selectedCashRecord, setSelectedCashRecord] = useState(null);
  const [cashAmount, setCashAmount] = useState("");
  const [processingCash, setProcessingCash] = useState(false);

  // State for detail modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  // Load comp-off records from API
  const loadCompOffRecords = async () => {
    setLoading(true);
    setError("");

    try {
      // Call your API endpoint
      const response = await axios.get(`${API_URL}/api/comp-off`);

      // Check if r esponse has data
      if (response.data && response.data.success) {
        // The data is in response.data.data
        setCompOffRecords(response.data.data || []);

        // If no data found
        if (!response.data.data || response.data.data.length === 0) {
          setError("No compensatory off records found in the database.");
        }
      } else {
        setError("API returned an unexpected response format.");
      }
    } catch (error) {
      console.error("Error loading comp-off records:", error);
      setError(`Failed to load data from server: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Separate records by type
  const leaveRecords = compOffRecords.filter(record => record.request_type === 'leave');
  const cashRecords = compOffRecords.filter(record => record.request_type === 'cash');

  // Open cash amount modal
  const openCashModal = (record) => {
    setSelectedCashRecord(record);
    setCashAmount(record.compensation_amount || "");
    setShowCashModal(true);
  };

  // Handle cash amount submission
  const handleCashSubmit = async () => {
    if (!cashAmount || isNaN(cashAmount) || parseFloat(cashAmount) <= 0) {
      alert("Please enter a valid cash amount.");
      return;
    }

    setProcessingCash(true);
    try {
      // Note: You'll need a PUT endpoint for this to work

      const response = await axios.put(
        `${API_URL}/api/comp-off/employee/${selectedCashRecord.employee_id}`,
        {
          created_at: selectedCashRecord.created_at,
          compensation_amount: parseFloat(cashAmount),
          status: "approved",
          id: selectedCashRecord.id,
          working_date: selectedCashRecord.working_date,
          request_type: selectedCashRecord.request_type,
          duration: selectedCashRecord.duration,
          compensatory_off_date: selectedCashRecord.compensatory_off_date,
          employee_name: selectedCashRecord.employee_name
        }
      );

      if (response.status === 200) {
        // Update local state
        setCompOffRecords(prev =>
          prev.map(record =>
            record.id === selectedCashRecord.id
              ? {
                ...record,
                compensation_amount: parseFloat(cashAmount),
                status: "approved"
              }
              : record
          )
        );
        setShowCashModal(false);
        setSelectedCashRecord(null);
        setCashAmount("");
        alert("Cash amount saved and record approved!");
      }
    } catch (error) {
      console.error("Error saving cash amount:", error);
      alert("Failed to save cash amount. PUT endpoint might not be implemented.");
    } finally {
      setProcessingCash(false);
    }
  };

  // View details
  const viewDetails = (record) => {
    setSelectedRecord(record);
    setShowDetailModal(true);
  };

  // Status badge color
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'approved': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'danger';
      case 'consumed': return 'info';
      default: return 'secondary';
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount || amount === 0 || amount === '0.00') return 'Not Set';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Render record row
  const renderRecordRow = (record, isCash = false) => (
    <tr
      key={record.id}
      style={{ cursor: 'pointer' }}
      onClick={() => viewDetails(record)}
    >
      {isAdmin && (
        <td>
          <div>
            <strong>{record.employee_name || `EMP-${record.employee_id}`}</strong>
            <br />
          </div>
        </td>
      )}

      {/* Working Date */}
      <td>
        {formatDate(record.working_date)}
      </td>

      {/* Duration */}
      <td>
        <Badge bg={record.duration && record.duration.includes('Full') ? 'primary' : 'warning'}>
          {record.duration || 'N/A'}
        </Badge>
      </td>

      {/* For Leave: Comp Off Date */}
      {!isCash && (
        <td>
          {formatDate(record.compensatory_off_date)}
        </td>
      )}

      {/* For Cash: Amount and Status with edit button */}
      {isCash && (
        <>
          <td>
            <div className="d-flex align-items-center justify-content-between">
              <span>{formatCurrency(record.compensation_amount)}</span>
              <Button
                variant="outline-success"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  openCashModal(record);
                }}
                title="Set Cash Amount"
              >
                <FaMoneyBillWave />
              </Button>

            </div>
          </td>
          <td>
            <Badge bg={getStatusBadgeColor(record.status)}>
              {record.status ? record.status.toUpperCase() : 'N/A'}
            </Badge>
          </td>
        </>
      )}

      {/* For Leave: Status column */}
      {!isCash && (
        <td>
          <Badge bg={getStatusBadgeColor(record.status)}>
            {record.status ? record.status.toUpperCase() : 'N/A'}
          </Badge>
        </td>
      )}
    </tr>
  );

  // Load data on component mount
  useEffect(() => {
    loadCompOffRecords();
  }, []);

  return (
    <div className="container-fluid py-1">

      {/* Summary Statistics */}
      {/* {!loading && compOffRecords.length > 0 && (
        <Card className="mt-4">
          <Card.Body>
            <h6 className="mb-3">Summary</h6>
            <Row>
              <Col md={3} className="text-center">
                <div className="border p-3 rounded">
                  <h4 className="text-primary">
                    {leaveRecords.filter(record => record.status === "approved").length}
                  </h4>
                  <p className="mb-0">Leave Requests</p>
                </div>
              </Col>
              <Col md={3} className="text-center">
                <div className="border p-3 rounded">
                  <h4 className="text-success">{cashRecords.length}</h4>
                  <p className="mb-0">Cash Requests</p>
                </div>
              </Col>
              <Col md={3} className="text-center">
                <div className="border p-3 rounded">
                  <h4 className="text-info">
                    {compOffRecords.filter(r => r.status === 'approved').length}
                  </h4>
                  <p className="mb-0">Approved</p>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )} */}
      <Card className="shadow-sm">
        <Card.Header className="bg-white">
          <h4 className="mb-0">
            <b>Compensatory Off Records</b>
          </h4>
        </Card.Header>

        <Card.Body>
          {/* Error/Success Messages */}
          {error && (
            <Alert variant="warning">
              {error}
            </Alert>
          )}

          {/* Records Display */}
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading records from API...</p>
            </div>
          ) : compOffRecords.length === 0 ? (
            <Alert variant="info">
              No compensatory off records found in the database.
            </Alert>
          ) : (
            <Row>
              {/* Leave Records (Left Column) */}
              <Col lg={6} md={12}>
                <Card className="mb-4">
                  <Card.Header className="bg-primary text-white">
                    <h5 className="mb-0 d-flex justify-content-between align-items-center">
                      <span>
                        <b>Leave Compensation</b>
                      </span>
                      <Badge bg="light" text="dark">
                        {leaveRecords ? leaveRecords.filter((e) => e.status === "approved").length : 0} records
                      </Badge>

                    </h5>
                  </Card.Header>
                  <Card.Body>
                    <div className="table-responsive">
                      {leaveRecords.length === 0 ? (
                        <Alert variant="info">
                          No leave compensation records found.
                        </Alert>
                      ) : (
                        <Table striped hover responsive>
                          <thead className="table-light">
                            <tr>
                              {isAdmin && <th>Employee</th>}
                              <th>Worked Date</th>
                              <th>Duration</th>
                              <th>Comp Off Date</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {leaveRecords.filter((element) => element.status === "approved").map(record => renderRecordRow(record, false))}
                          </tbody>
                        </Table>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              {/* Cash Records (Right Column) */}
              <Col lg={6} md={12}>
                <Card className="mb-4">
                  <Card.Header className="bg-success text-white">
                    <h5 className="mb-0 d-flex justify-content-between align-items-center">
                      <span>
                        <b>Cash Compensation</b>
                      </span>
                      <Badge bg="light" text="dark">
                        {cashRecords.filter((record) => (record.status === "pending" || record.status === "approved")).length} records
                      </Badge>
                    </h5>
                  </Card.Header>
                  <Card.Body>
                    <div className="table-responsive">
                      {cashRecords.length === 0 ? (
                        <Alert variant="info">
                          No cash compensation records found.
                        </Alert>
                      ) : (
                        <Table striped hover responsive>
                          <thead className="table-light">
                            <tr>
                              {isAdmin && <th>Employee</th>}
                              <th>Worked Date</th>
                              <th>Duration</th>
                              <th>Amount</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {cashRecords.filter((record) => (record.status === "pending" || record.status == "approved")).map(record => renderRecordRow(record, true))}
                          </tbody>
                        </Table>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}


        </Card.Body>
      </Card>

      {/* Cash Amount Modal */}
      <Modal
        show={showCashModal}
        onHide={() => {
          setShowCashModal(false);
          setSelectedCashRecord(null);
          setCashAmount("");
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Set Cash Compensation Amount</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedCashRecord && (
            <>
              <div className="mb-3">
                <p><strong>Employee:</strong> {selectedCashRecord.employee_name || `EMP-${selectedCashRecord.employee_id}`}</p>
                <p><strong>Worked Date:</strong> {formatDate(selectedCashRecord.working_date)}</p>
                <p><strong>Duration:</strong> {selectedCashRecord.duration}</p>
                <p>
                  <strong>Current Status:</strong>{" "}
                  <Badge bg={getStatusBadgeColor(selectedCashRecord.status)}>
                    {selectedCashRecord.status ? selectedCashRecord.status.toUpperCase() : 'N/A'}
                  </Badge>
                </p>
              </div>

              <Form.Group className="mb-3">
                <Form.Label>
                  <strong>Cash Amount (₹)</strong>
                </Form.Label>
                <Form.Control
                  type="number"
                  value={cashAmount}
                  onChange={(e) => setCashAmount(e.target.value)}
                  placeholder="Enter amount"
                  min="0"
                  step="0.01"
                  autoFocus
                />
                <Form.Text className="text-muted">
                  Enter the compensation amount for this worked day.
                </Form.Text>
              </Form.Group>

              <Alert variant="info">
                <strong>Note:</strong> Setting the cash amount will automatically approve this record.
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowCashModal(false);
              setSelectedCashRecord(null);
              setCashAmount("");
            }}
            disabled={processingCash}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCashSubmit}
            disabled={processingCash || !cashAmount}
          >
            {processingCash ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Processing...
              </>
            ) : (
              'Save & Approve'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Detail Modal */}
      <Modal
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Compensatory Off Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRecord && (
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <h6>Record ID</h6>
                  <p className="text-muted">{selectedRecord.id || 'N/A'}</p>
                </div>

                <div className="mb-3">
                  <h6>Employee</h6>
                  <p className="text-muted">
                    {selectedRecord?.employee_name}
                    <br />
                  </p>
                </div>

                <div className="mb-3">
                  <h6>Working Date</h6>
                  <p className="text-muted">{formatDate(selectedRecord.working_date)}</p>
                </div>

                <div className="mb-3">
                  <h6>Duration</h6>
                  <p>
                    <Badge bg={selectedRecord.duration && selectedRecord.duration.includes('Full') ? 'primary' : 'warning'}>
                      {selectedRecord.duration || 'N/A'}
                    </Badge>
                  </p>
                </div>
              </Col>

              <Col md={6}>
                <div className="mb-3">
                  <h6>Request Type</h6>
                  <p>
                    <Badge bg={selectedRecord.request_type === 'leave' ? 'primary' : 'success'}>
                      {selectedRecord.request_type ? selectedRecord.request_type.toUpperCase() : 'N/A'}
                    </Badge>
                  </p>
                </div>

                {selectedRecord.request_type === "leave" && (
                  <div className="mb-3">
                    <h6>Compensatory Off Date</h6>
                    <p className="text-muted">{formatDate(selectedRecord.compensatory_off_date)}</p>
                  </div>
                )}

                {selectedRecord.request_type === 'cash' && (
                  <div className="mb-3">
                    <h6>Compensation Amount</h6>
                    <p className="text-muted">
                      {selectedRecord.compensation_amount
                        ? formatCurrency(selectedRecord.compensation_amount)
                        : 'Not Set'}
                    </p>
                  </div>
                )}

                <div className="mb-3">
                  <h6>Status</h6>
                  <p>
                    <Badge bg={getStatusBadgeColor(selectedRecord.status)}>
                      {selectedRecord.status ? selectedRecord.status.toUpperCase() : 'N/A'}
                    </Badge>
                  </p>
                </div>
              </Col>

              <Col md={12}>
                <hr />
                <div className="mb-3">
                  <h6>Created On</h6>
                  <p className="text-muted">
                    {selectedRecord.created_at ? new Date(selectedRecord.created_at).toLocaleString() : 'N/A'}
                  </p>
                </div>

                {selectedRecord.request_type === 'cash' && selectedRecord.updated_at && selectedRecord.updated_at !== selectedRecord.created_at && (
                  <div className="mb-3">
                    <h6>Last Updated</h6>
                    <p className="text-muted">
                      {new Date(selectedRecord.updated_at).toLocaleString()}
                    </p>
                  </div>
                )}
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          {selectedRecord && selectedRecord.request_type === 'cash' && isAdmin && selectedRecord.status === 'pending' && (
            <Button
              variant="primary"
              onClick={() => {
                setShowDetailModal(false);
                setTimeout(() => openCashModal(selectedRecord), 300);
              }}
            >
              Set Cash Amount
            </Button>
          )}
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CompOffDisplayForm;